import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import * as apiInit from "../utils/ApiInit.ts";
import Icon from "../components/widgets/Icon";

interface InitDataNewOrderContextInterface {
  tiposDocumentosTributarios: any[];
  condicionesPago: any[];
  isTutorialActive: boolean;
  setIsTutorialActive: Dispatch<SetStateAction<any>>;
}

const defaultState: InitDataNewOrderContextInterface = {
  tiposDocumentosTributarios: [],
  condicionesPago: [],
  isTutorialActive: false,
  setIsTutorialActive: () => {},
};

const InitDataNewOrderContext = createContext(defaultState);

type InitDataNewOrderProviderProps = {
  children: React.ReactNode;
};

const InitDataNewOrderProvider = ({
  children,
}: InitDataNewOrderProviderProps) => {
  const componentIsMounted = useRef(true);
  const [tiposDocumentosTributarios, setTiposDocumentosTributarios] = useState<
    any[]
  >([]);
  const [condicionesPago, setCondicionesPago] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTutorialActive, setIsTutorialActive] = useState(false);

  // Efecto para el Tutorial (se mantiene igual)
  useEffect(() => {
    const isTutorialActiveVar = localStorage.getItem("isTutorialActive");
    if (isTutorialActiveVar === "true") {
      setIsTutorialActive(true);
    } else {
      setIsTutorialActive(false);
    }
  }, []);

  // Función unificada para cargar datos
  const fetchInitDataTiposDocTributarios = async () => {
    try {
      const storedData = localStorage.getItem("tiposDocumentosTributarios");

      if (storedData) {
        // CASO 1: Datos existen en LocalStorage
        if (componentIsMounted.current) {
          setTiposDocumentosTributarios(JSON.parse(storedData));
          setLoading(false);
        }
      } else {
        // CASO 2: Datos no existen, llamar a la API
        const response =
          await apiInit.GET_INIT_DATA_DOC_TRIBUTARIOS.getTiposDocTributarios();

        if (componentIsMounted.current) {
          if (response) {
            localStorage.setItem(
              "tiposDocumentosTributarios",
              JSON.stringify(response),
            );
            setTiposDocumentosTributarios(response);
          }
          // Quitamos el loading una vez que la API responde
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error al cargar tipos tributarios:", error);
      if (componentIsMounted.current) {
        setLoading(false); // Quitamos loading incluso si hay error para no bloquear la UI
      }
    }
  };

  const fetchInitDataCondicionesPago = async () => {
    try {
      const storedDataCp = localStorage.getItem("condicionesPago");

      if (storedDataCp) {
        // CASO 1: Datos existen en LocalStorage
        if (componentIsMounted.current) {
          setCondicionesPago(JSON.parse(storedDataCp));
          setLoading(false);
        }
      } else {
        // CASO 2: Datos no existen, llamar a la API
        const response =
          await apiInit.GET_INIT_DATA_CONDICIONES_PAGO.getCondicionesPago();

        if (componentIsMounted.current) {
          if (response) {
            localStorage.setItem("condicionesPago", JSON.stringify(response));
            setCondicionesPago(response);
          }
          // Quitamos el loading una vez que la API responde
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error al cargar condiciones de pago:", error);
      if (componentIsMounted.current) {
        setLoading(false); // Quitamos loading incluso si hay error para no bloquear la UI
      }
    }
  };

  useEffect(() => {
    componentIsMounted.current = true;

    // Llamamos a la función asíncrona
    fetchInitDataTiposDocTributarios();
    fetchInitDataCondicionesPago();

    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  // Renderizado
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Icon
          icon={"spinner"}
          className="fa-spin"
          style={{ fontSize: "50px" }}
        />
      </div>
    );
  }

  return (
    <InitDataNewOrderContext.Provider
      value={{
        tiposDocumentosTributarios,
        condicionesPago,
        isTutorialActive,
        setIsTutorialActive,
      }}
    >
      {children}
    </InitDataNewOrderContext.Provider>
  );
};

export { InitDataNewOrderContext, InitDataNewOrderProvider };
